<template>
  <div class="settings-container">
    <div class="container py-4">
      <!-- Step Header -->
      <div class="step-header mb-4">
        <h2 class="text-center">Step 6: Dashboard Settings</h2>
        <p class="text-center text-muted">
          Configure default settings and behavior for your dashboard
        </p>
      </div>

      <div class="row justify-content-center">
        <div class="col-lg-8">
          <!-- Default Selections Card -->
          <b-card class="settings-card mb-4">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="fas fa-sliders-h mr-2 text-primary"></i>
                <strong>Default Selections</strong>
              </div>
            </template>

            <b-form-group
              label="Default Indicator"
              description="The indicator that will be selected when the dashboard loads"
            >
              <b-form-select
                v-model="settings.defaultIndicator"
                :options="indicatorOptions"
              >
                <template #first>
                  <b-form-select-option :value="null">
                    -- First available indicator --
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>

            <b-form-group
              label="Default Location"
              description="The location that will be selected when the dashboard loads"
            >
              <b-form-select
                v-model="settings.defaultLocation"
                :options="locationOptions"
              >
                <template #first>
                  <b-form-select-option :value="null">
                    -- All locations --
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>

            <b-form-group
              label="Default Period"
              description="The time period that will be selected when the dashboard loads"
            >
              <b-form-select
                v-model="settings.defaultPeriod"
                :options="periodOptions"
              >
                <template #first>
                  <b-form-select-option :value="null">
                    -- Latest period --
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>

            <b-form-group
              label="Default Data Source"
              description="The data source that will be selected when the dashboard loads"
            >
              <b-form-select
                v-model="settings.defaultDatasource"
                :options="datasourceOptions"
              >
                <template #first>
                  <b-form-select-option :value="null">
                    -- All data sources --
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-card>

          <!-- Display Options Card -->
          <b-card class="settings-card mb-4">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="fas fa-tv mr-2 text-info"></i>
                <strong>Display Options</strong>
              </div>
            </template>

            <b-form-group>
              <b-form-checkbox v-model="settings.showSummaryCards" switch>
                Show Summary Cards
                <small class="text-muted d-block">
                  Display overview statistics at the top of the dashboard
                </small>
              </b-form-checkbox>
            </b-form-group>

            <b-form-group>
              <b-form-checkbox v-model="settings.showDataTable" switch>
                Show Data Table
                <small class="text-muted d-block">
                  Display a tabular view of the data at the bottom
                </small>
              </b-form-checkbox>
            </b-form-group>

            <b-form-group>
              <b-form-checkbox v-model="settings.showFilters" switch>
                Show Filter Controls
                <small class="text-muted d-block">
                  Allow users to filter data by indicator, location, period, etc.
                </small>
              </b-form-checkbox>
            </b-form-group>

            <b-form-group>
              <b-form-checkbox v-model="settings.showExportButton" switch>
                Show Export Button
                <small class="text-muted d-block">
                  Allow users to export data as CSV
                </small>
              </b-form-checkbox>
            </b-form-group>

            <b-form-group>
              <b-form-checkbox v-model="settings.showTrendAnalysis" switch>
                Show Trend Analysis
                <small class="text-muted d-block">
                  Display trend/time series charts for the data
                </small>
              </b-form-checkbox>
            </b-form-group>
          </b-card>

          <!-- Chart Options Card -->
          <b-card class="settings-card mb-4">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="fas fa-chart-bar mr-2 text-success"></i>
                <strong>Chart Options</strong>
              </div>
            </template>

            <b-form-group
              label="Default Chart Type"
              description="The chart type to use for the indicator overview"
            >
              <b-form-select v-model="settings.defaultChartType" :options="chartTypeOptions">
              </b-form-select>
            </b-form-group>

            <b-form-group
              label="Aggregation Method"
              description="How to aggregate values when grouping data"
            >
              <b-form-select v-model="settings.aggregationMethod" :options="aggregationOptions">
              </b-form-select>
            </b-form-group>

            <b-form-group>
              <b-form-checkbox v-model="settings.enableChartAnimation" switch>
                Enable Chart Animations
                <small class="text-muted d-block">
                  Animate charts when data changes
                </small>
              </b-form-checkbox>
            </b-form-group>
          </b-card>

          <!-- Advanced Settings Card -->
          <b-card class="settings-card">
            <template #header>
              <div class="d-flex align-items-center">
                <i class="fas fa-cog mr-2 text-warning"></i>
                <strong>Advanced Settings</strong>
              </div>
            </template>

            <b-form-group
              label="Rows Per Page"
              description="Number of rows to show in the data table"
            >
              <b-form-select v-model="settings.rowsPerPage" :options="rowsPerPageOptions">
              </b-form-select>
            </b-form-group>

            <b-form-group>
              <b-form-checkbox v-model="settings.enableOfflineMode" switch>
                Enable Offline Mode
                <small class="text-muted d-block">
                  Cache data locally for offline access
                </small>
              </b-form-checkbox>
            </b-form-group>

            <b-form-group>
              <b-form-checkbox v-model="settings.autoRefresh" switch>
                Auto-Refresh Data
                <small class="text-muted d-block">
                  Automatically refresh data periodically
                </small>
              </b-form-checkbox>
            </b-form-group>

            <b-form-group v-if="settings.autoRefresh">
              <label>Refresh Interval (minutes)</label>
              <b-form-input
                v-model.number="settings.refreshInterval"
                type="number"
                min="1"
                max="60"
              ></b-form-input>
            </b-form-group>
          </b-card>
        </div>
      </div>

      <!-- Navigation Buttons -->
      <div class="navigation-buttons mt-4 d-flex justify-content-between">
        <b-button variant="outline-secondary" @click="goBack">
          <i class="fas fa-arrow-left mr-2"></i>
          Back to Appearance
        </b-button>
        <b-button variant="primary" @click="saveAndContinue">
          Continue to Preview
          <i class="fas fa-arrow-right ml-2"></i>
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';

export default {
  name: 'SettingsConfig',

  data() {
    return {
      settings: {
        // Default selections
        defaultIndicator: null,
        defaultLocation: null,
        defaultPeriod: null,
        defaultDatasource: null,

        // Display options
        showSummaryCards: true,
        showDataTable: true,
        showFilters: true,
        showExportButton: true,
        showTrendAnalysis: true,

        // Chart options
        defaultChartType: 'bar',
        aggregationMethod: 'avg',
        enableChartAnimation: true,

        // Advanced settings
        rowsPerPage: 10,
        enableOfflineMode: false,
        autoRefresh: false,
        refreshInterval: 5,
      },
      chartTypeOptions: [
        { value: 'bar', text: 'Bar Chart' },
        { value: 'column', text: 'Column Chart' },
        { value: 'line', text: 'Line Chart' },
        { value: 'area', text: 'Area Chart' },
      ],
      aggregationOptions: [
        { value: 'sum', text: 'Sum' },
        { value: 'avg', text: 'Average' },
        { value: 'min', text: 'Minimum' },
        { value: 'max', text: 'Maximum' },
        { value: 'count', text: 'Count' },
      ],
      rowsPerPageOptions: [
        { value: 5, text: '5 rows' },
        { value: 10, text: '10 rows' },
        { value: 25, text: '25 rows' },
        { value: 50, text: '50 rows' },
        { value: 100, text: '100 rows' },
      ],
    };
  },

  computed: {
    ...mapState('configurer', ['dashboardConfig', 'dataSummary']),
    ...mapGetters('configurer', [
      'availableIndicators',
      'availableLocations',
      'availablePeriods',
      'availableDatasources',
    ]),

    indicatorOptions() {
      return this.availableIndicators.map((ind) => ({ value: ind, text: ind }));
    },

    locationOptions() {
      return this.availableLocations.map((loc) => ({ value: loc, text: loc }));
    },

    periodOptions() {
      return this.availablePeriods.map((period) => ({ value: period, text: period }));
    },

    datasourceOptions() {
      return this.availableDatasources.map((src) => ({ value: src, text: src }));
    },
  },

  mounted() {
    // Load existing settings from config
    if (this.dashboardConfig?.settings) {
      this.settings = { ...this.settings, ...this.dashboardConfig.settings };
    }
  },

  methods: {
    ...mapMutations('configurer', ['UPDATE_DASHBOARD_CONFIG']),

    goBack() {
      this.$router.push({ name: 'configure-appearance' });
    },

    saveAndContinue() {
      // Save settings to dashboard config
      this.UPDATE_DASHBOARD_CONFIG({ settings: this.settings });

      // Navigate to preview
      this.$router.push({ name: 'configure-preview' });
    },
  },
};
</script>

<style scoped>
.settings-container {
  min-height: 100vh;
  /* background-color: #f8f9fa; Removed */
  padding-bottom: 2rem;
}

.step-header h2 {
  color: #2c3e50;
  font-weight: 700;
}

/* Glass Cards */
.settings-card {
  border: none;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.1);
  margin-bottom: 24px;
}

::v-deep .card-header {
  background-color: rgba(255, 255, 255, 0.5);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding: 1.25rem;
  border-radius: 20px 20px 0 0 !important;
}

/* Form Styles */
::v-deep .custom-select {
    background: rgba(255, 255, 255, 0.5);
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 12px;
    height: 48px;
    box-shadow: none;
}

::v-deep .custom-select:focus {
    background: #ffffff;
    border-color: #007D53;
    box-shadow: 0 0 0 3px rgba(0, 125, 83, 0.1);
}

::v-deep .custom-switch .custom-control-label::before {
    background-color: #e9ecef;
    border-color: #adb5bd;
}

::v-deep .custom-switch .custom-control-input:checked ~ .custom-control-label::before {
    background-color: #007D53;
    border-color: #007D53;
}

.custom-control-label {
  font-weight: 600;
  color: #2c3e50;
  padding-top: 2px;
}

.custom-control-label small {
  font-weight: 400;
  color: #636e72;
  margin-top: 2px;
}

/* Navigation */
.navigation-buttons {
  padding: 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  background-color: rgba(255, 255, 255, 0.5);
  margin-top: 2rem;
  border-radius: 0 0 20px 20px;
  backdrop-filter: blur(10px);
}

::v-deep .btn-primary {
  background-color: #007D53;
  border-color: #007D53;
  border-radius: 10px;
  padding: 0.5rem 1.5rem;
}

::v-deep .btn-outline-secondary {
    border-radius: 10px;
    border-color: #b2bec3;
    color: #636e72;
}
</style>
